import PersonIcon from "@mui/icons-material/PersonOutlineRounded";
import { useMemo, useState } from "react";
import type { PortalWidget } from "@entrilia/shared/api/portalWidgetsTypes";
import MultiSelectFilter from "@entrilia/shared/components/MultiSelectFilter";
import CompaniesIcon from "@entrilia/shared/icons/CompaniesIcon";
import { convertISODate } from "@entrilia/shared/utilities/dateUtils";
import SwipeableFiltersDrawer from "../../common/reports/mobile/SwipeableFiltersDrawer";
import ReportingDateSelect from "../filters/ReportingDateSelect";
import { useBalanceWidgetData } from "../widgetHooks";
import WidgetListDialog from "./WidgetListDialog";
import WidgetMobileContent from "./WidgetMobileContent";

interface Props {
  widgetSettings: PortalWidget;
  onClose: () => void;
}

const BalanceWidgetListDialog = ({ widgetSettings, onClose }: Props) => {
  const {
    parameters,
    report,
    fetchError,
    filteredRows,
    isRunningReport,
    noDataText,
    setSearchValue,
    reportingDate,
    setReportingDate,
    investorIds,
    setInvestorIds,
    fundIds,
    setFundIds,
    clearFilters,
    isAnyFilterApplied,
  } = useBalanceWidgetData(widgetSettings);

  const [isFiltersOpen, setIsFiltersOpen] = useState(false);

  const filterText = useMemo(() => {
    const investorNames =
      investorIds.length > 0 && parameters?.investors
        ? parameters.investors
            .filter((inv) => investorIds.includes(inv.id))
            .map((inv) => inv.name)
            .join(", ")
        : "";

    const fundNames =
      fundIds.length > 0 && parameters?.funds
        ? parameters.funds
            .filter((fund) => fundIds.includes(fund.id))
            .map((fund) => fund.name)
            .join(", ")
        : "";

    return [convertISODate(reportingDate), investorNames, fundNames].filter((part) => part !== "").join(" | ");
  }, [fundIds, investorIds, parameters, reportingDate]);

  return (
    <>
      <WidgetListDialog
        widgetSettings={widgetSettings}
        onSearch={setSearchValue}
        onOpenFilters={() => setIsFiltersOpen(true)}
        onClose={onClose}
        rowCount={filteredRows.length}
        loading={isRunningReport}
        isAnyFilterApplied={isAnyFilterApplied}
        filterText={filterText}
      >
        <WidgetMobileContent
          report={report}
          rows={filteredRows}
          columnsLimit={7}
          noDataText={noDataText}
          loading={isRunningReport}
          fetchError={fetchError}
        />
      </WidgetListDialog>

      <SwipeableFiltersDrawer
        open={isFiltersOpen}
        onClose={() => setIsFiltersOpen(false)}
        isAnyFilterApplied={isAnyFilterApplied}
        onClearFilters={clearFilters}
        disabled={isRunningReport}
      >
        <ReportingDateSelect
          dates={parameters?.reportingDates ?? []}
          selectedDate={reportingDate}
          onDateChange={setReportingDate}
          disabled={isRunningReport}
          fullWidth
        />

        <MultiSelectFilter
          options={parameters?.investors ?? []}
          selectedValues={investorIds}
          onChange={setInvestorIds}
          getOptionValue={(inv) => inv.id}
          getOptionLabel={(inv) => inv.name}
          placeholder="Investors"
          StartIcon={PersonIcon}
          disabled={isRunningReport || !parameters?.investors.length}
          fullWidth
        />

        <MultiSelectFilter
          options={parameters?.funds ?? []}
          selectedValues={fundIds}
          onChange={setFundIds}
          getOptionValue={(fund) => fund.id}
          getOptionLabel={(fund) => fund.name}
          placeholder="Funds"
          StartIcon={CompaniesIcon}
          disabled={isRunningReport || !parameters?.funds.length}
          fullWidth
        />
      </SwipeableFiltersDrawer>
    </>
  );
};

export default BalanceWidgetListDialog;
